import React from "react";
import { Typography } from "@material-ui/core";
import type { Selection, SelectionType, PickType, PickedItem, MountedEditor } from "../uiTypes";
import QuestionEditor from "./editors/QuestionEditor";
import QuestionnaireEditor from "./editors/QuestionnaireEditor";
import QuestionGroupEditor from "./editors/QuestionGroupEditor";
import { editorStyles as styles } from "./editors/editorStyles";

type EditorProps = {
  selection: Selection;
  isExpanded: boolean;
  onToggleExpand: () => void;
  currentPageIndex?: number;
  onPageIndexChange?: (index: number) => void;
  onRequestPick: (pickType: PickType) => void;
  pickedItem: PickedItem;
  onPickConsumed: () => void;
  isPicking: boolean;
  highlight?: string | null;
  onHighlightConsumed?: () => void;
  onDeleted?: () => void;
  onUnsavedChange?: (type: SelectionType, id: number, dirty: boolean) => void;
};

const MOUNTED_EDITOR_LIMIT = 3;

const Editor: React.VFC<EditorProps> = ({
  selection,
  isExpanded,
  onToggleExpand,
  currentPageIndex,
  onPageIndexChange,
  onRequestPick,
  pickedItem,
  onPickConsumed,
  isPicking,
  highlight,
  onHighlightConsumed,
  onDeleted,
  onUnsavedChange,
}) => {
  const [mounted, setMounted] = React.useState<MountedEditor[]>([]);

  const onQuestionUnsaved = React.useCallback(
    (eid: number, dirty: boolean) => onUnsavedChange?.("question", eid, dirty),
    [onUnsavedChange]
  );
  const onQuestionnaireUnsaved = React.useCallback(
    (eid: number, dirty: boolean) => onUnsavedChange?.("questionnaire", eid, dirty),
    [onUnsavedChange]
  );
  const onQuestionGroupUnsaved = React.useCallback(
    (eid: number, dirty: boolean) => onUnsavedChange?.("questionGroup", eid, dirty),
    [onUnsavedChange]
  );

  React.useEffect(() => {
    if (!selection) return;
    setMounted(prev => {
      const without = prev.filter(m => !(m.type === selection.type && m.id === selection.id));
      const next = [...without, { type: selection.type, id: selection.id }];
      return next.slice(-MOUNTED_EDITOR_LIMIT);
    });
  }, [selection]);

  if (!selection) {
    return (
      <div className={styles.empty}>
        <Typography color="textSecondary">Select an item to edit</Typography>
      </div>
    );
  }

  return (
    <>
      {mounted.map(m => {
        const isActive = m.type === selection.type && m.id === selection.id;
        if (m.type === "question") {
          return (
            <div key={`question-${m.id}`} style={{ display: isActive ? undefined : "none", height: "100%" }}>
              <QuestionEditor
                id={m.id}
                isExpanded={isExpanded}
                onToggleExpand={onToggleExpand}
                onDeleted={onDeleted}
                onUnsavedChange={onUnsavedChange ? onQuestionUnsaved : undefined}
              />
            </div>
          );
        }
        if (m.type === "questionnaire") {
          return (
            <div key={`questionnaire-${m.id}`} style={{ display: isActive ? undefined : "none", height: "100%" }}>
              <QuestionnaireEditor
                id={m.id}
                isExpanded={isExpanded}
                onToggleExpand={onToggleExpand}
                currentPageIndex={currentPageIndex}
                onPageIndexChange={onPageIndexChange}
                onRequestPick={onRequestPick}
                pickedItem={pickedItem}
                onPickConsumed={onPickConsumed}
                isPicking={isPicking}
                highlight={isActive ? highlight : null}
                onHighlightConsumed={isActive ? onHighlightConsumed : undefined}
                onDeleted={onDeleted}
                onUnsavedChange={onUnsavedChange ? onQuestionnaireUnsaved : undefined}
              />
            </div>
          );
        }
        return (
          <div key={`questionGroup-${m.id}`} style={{ display: isActive ? undefined : "none", height: "100%" }}>
            <QuestionGroupEditor
              id={m.id}
              isExpanded={isExpanded}
              onToggleExpand={onToggleExpand}
              onRequestPick={onRequestPick}
              pickedItem={pickedItem}
              onPickConsumed={onPickConsumed}
              isPicking={isPicking}
              highlight={isActive ? highlight : null}
              onHighlightConsumed={isActive ? onHighlightConsumed : undefined}
              onDeleted={onDeleted}
              onUnsavedChange={onUnsavedChange ? onQuestionGroupUnsaved : undefined}
            />
          </div>
        );
      })}
    </>
  );
};

export default Editor;
